import type { Locale } from '@gr/core/platform/user-preferences/locales';
import { zonedParts } from '@gr/core/time/zoned-time';

// Форматирование через Intl по текущему языку; время — в поясе блога.

export function formatDateTime(iso: string | null, locale: Locale, timeZone?: string): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(iso));
}

export function formatDate(iso: string | null, locale: Locale, timeZone?: string): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone }).format(new Date(iso));
}

export function formatTime(iso: string, locale: Locale, timeZone?: string): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone }).format(new Date(iso));
}

export function formatWeekday(weekday: number, locale: Locale, style: 'short' | 'long' = 'short'): string {
  // 2024-01-01 — понедельник.
  return new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, weekday)));
}

export function formatFileSize(bytes: number, locale: Locale): string {
  const units = locale === 'ru' ? ['Б', 'КБ', 'МБ', 'ГБ'] : ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: unit === 0 ? 0 : 1 }).format(value)} ${units[unit]}`;
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale).format(value);
}

/** Значение для <input type="datetime-local"> в поясе блога. */
export function toLocalInputValue(iso: string, timeZone: string): string {
  const p = zonedParts(new Date(iso), timeZone);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** Обратное: значение datetime-local в поясе блога → ISO UTC. */
export function fromLocalInputValue(value: string, timeZone: string, toUtc: (local: { year: number; month: number; day: number; hour: number; minute: number }, tz: string) => Date): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, m, d, h, min] = match.map(Number) as number[];
  return toUtc({ year: y!, month: m!, day: d!, hour: h!, minute: min! }, timeZone).toISOString();
}

/** Оценка звёздами, как в тексте поста: ⭐️ за целую, ✨ за половину. */
export function stars(rating: number): string {
  const full = Math.floor(rating);
  return '⭐️'.repeat(full) + (rating - full >= 0.5 ? '✨' : '');
}
